import { IViewerFiltersData, prepareSegments } from 'components/element-viewer';
import { FilterType, getFiltersStateBuilder } from 'components/external-reference/Filter';
import { SiblingTabType } from 'app/extreport/viewer/SiblingTabsMenu';
import { useEffect, useMemo, useState } from 'react';
import { BookmarkType } from 'components/external-reference/bookmark/BookmarkDropdown';
import {
    applyBookmarkToFilters,
    getNewBookmarkObj,
    setBookmarkReferenceId,
} from 'components/external-reference/bookmark/Bookmark';
import { externalReportAPI } from 'api/external-report';
import { useGetSegmentValueIdFromFilters } from 'app/extreport/ExternalReportViewer';
import { arrayMoveImmutable } from 'array-move';
import useBundleTranslation from 'i18n';
import { ManagerContextType } from 'components/external-reference/bookmark/BookmarManager';
import { DashboardElementType } from 'tools/types';
import { DisplayMask } from '@report-content';

export interface IBookmarksContext {
    showIcon?: boolean;
    filters: Array<FilterType>;
    siblingTabs: Array<SiblingTabType>;
    bookmarks: Array<BookmarkType>;
    selectedReferenceId: number;
    APIUpdateBookmark: (bookmark: BookmarkType | null, selectedReferenceId: number) => void;
    APISaveAsNewBookmark: (bookmark: BookmarkType | null, selectedReferenceId: number) => void;
    APIDeleteBookmark: (bookmarkId: number) => void;
    APIDuplicateBookmark: (bookmarkId: number) => void;
    APISortBookmarks: (oldIndex: number, newIndex: number) => void;
    APIIsLoading: APILoadingType;
}

export type APILoadingType = 'delete' | 'save' | 'update' | 'copy' | 'sort' | false;

export interface IBookmarkElementContext {
    elementId: number;
    elementType: DashboardElementType;
    segmentData: Array<FilterType>;
    siblingTabs?: Array<SiblingTabType>;
    distributionItemId?: number;
    displayMasks?: DisplayMask[];
}

function getUniqueByFilterId(arr: FilterType[]) {
    const seen = new Set();
    return arr.filter((item) => {
        if (seen.has(item.filterId)) {
            return false;
        }
        seen.add(item.filterId);
        return true;
    });
}

export default function useBookmarks<T extends IViewerFiltersData>(
    viewerFiltersData: T,
    elementContext: IBookmarkElementContext,
    handleApplyBookmark?: (bookmarkId: number) => void,
) {
    const [bookmarks, setBookmarks] = useState<Array<BookmarkType>>([]);
    const [shouldSelectNewBookmark, setShouldSelectNewBookmark] = useState(false);
    const [filtersData, setFiltersData] = useState<T>();
    const [appliedBookmarkId, setAppliedBookmarkId] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<APILoadingType>(false);
    const { t } = useBundleTranslation(['components/external-reference/bookmark']);
    const { filtersList, segmentValuesMap } = useMemo(() => {
        const { filtersList, segmentValuesMap } = prepareSegments(
            structuredClone(viewerFiltersData.filtersList),
            elementContext.segmentData,
        );
        return { filtersList: getUniqueByFilterId(filtersList), segmentValuesMap };
    }, [JSON.stringify(viewerFiltersData), JSON.stringify(elementContext.segmentData)]);

    useEffect(() => {
        if (!shouldSelectNewBookmark || !handleApplyBookmark || bookmarks.length != 1) {
            setShouldSelectNewBookmark(false);
            return;
        }
        handleApplyBookmark(bookmarks[0].id);
        setShouldSelectNewBookmark(false);
    }, [shouldSelectNewBookmark]);

    const findBookmarkIndexById = function (bookmarkId: number): number {
        return bookmarks.findIndex((bookmark) => bookmark.id == bookmarkId);
    };

    const elementId = elementContext.elementId;
    const elementType = elementContext.elementType;
    const siblingTabs = useMemo<Array<SiblingTabType>>(
        () => elementContext?.siblingTabs ?? [],
        [elementContext.siblingTabs],
    );

    // Save bookmark changes
    const APIUpdateBookmark = function (
        bookmark: BookmarkType | null,
        selectedReferenceId: number,
        actualFilters?: FilterType[],
    ) {
        let filtersState = actualFilters ? actualFilters.slice() : filtersList.slice();
        if (!bookmark) {
            // If bookmark empty update AppliedBookmark
            // Can be called from BookmarkDropDown
            if (appliedBookmarkId == 0) {
                // Save as new
                return APISaveAsNewBookmark(null, selectedReferenceId);
            }
            bookmark = bookmarks[findBookmarkIndexById(appliedBookmarkId)];
            // Update bookmark filters list
            bookmark.filtersList = filtersState.map((filter) => ({
                filterId: filter.filterId,
                values: filter.values.filter((v) => filter.selectedValues.includes(v.value_id)),
                selectedValues: filter.selectedValues,
                filterInput: filter.filterInput,
            }));
            // Update bookmark referenceId
            const tab = siblingTabs.find((t) => t.referenceId == selectedReferenceId);
            setBookmarkReferenceId(bookmark, tab ?? false);
        }

        if (bookmark.id == 0) {
            return APISaveAsNewBookmark(bookmark, selectedReferenceId);
        }

        filtersState = applyBookmarkToFilters(filtersState, bookmark);
        // POST to server
        let segmentValueId = useGetSegmentValueIdFromFilters(filtersState);
        setIsLoading('update');
        // Update state
        const list = bookmarks.slice();
        list[findBookmarkIndexById(bookmark.id)] = bookmark;
        externalReportAPI.bookmark
            .updateBookmark(
                elementId,
                elementType,
                segmentValueId,
                bookmark,
                JSON.stringify(getFiltersStateBuilder(filtersState.filter((f) => f.filterId > 0))),
                elementContext.distributionItemId,
            )
            .then((response) => {
                setBookmarks(list);
            })
            .finally(() => setIsLoading(false));
    };

    const APISaveAsNewBookmark = function (
        bookmark: BookmarkType | null,
        selectedReferenceId: number,
        actualFilters?: FilterType[],
    ) {
        let filtersState = actualFilters ? actualFilters.slice() : filtersList.slice();
        if (!bookmark) {
            bookmark = getNewBookmarkObj();
            bookmark.filtersList = filtersState.map((filter) => ({
                filterId: filter.filterId,
                values: filter.values.filter((v) => filter.selectedValues.includes(v.value_id)),
                selectedValues: filter.selectedValues,
                filterInput: filter.filterInput,
            }));
            // Update bookmark referenceId
            const tab = siblingTabs.find((t) => t.referenceId == selectedReferenceId);
            setBookmarkReferenceId(bookmark, tab ?? false);
        } else {
            filtersState = applyBookmarkToFilters(filtersState, bookmark);
        }

        let segmentValueId = useGetSegmentValueIdFromFilters(filtersState);
        setIsLoading('save');
        externalReportAPI.bookmark
            .updateBookmark(
                elementId,
                elementType,
                segmentValueId,
                bookmark,
                JSON.stringify(getFiltersStateBuilder(filtersState.filter((f) => f.filterId > 0))),
                elementContext.distributionItemId,
            )
            .then((response) => {
                if (response.data!.status == 'OK' && bookmark) {
                    bookmark.id = response.data.data.id;
                    bookmark.displayOrder = response.data.data.displayOrder;
                    const list = bookmarks.slice();
                    list.push(bookmark);
                    setBookmarks(list);
                    setShouldSelectNewBookmark(true);
                } else {
                    console.log(response);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const APIDuplicateBookmark = (bookmarkId: number) => {
        setIsLoading('copy');
        externalReportAPI.bookmark
            .duplicateBookmark(elementId, elementType, bookmarkId, elementContext.distributionItemId)
            .then((response) => {
                if (response.data!.status == 'OK') {
                    const bookmark = getNewBookmarkObj(bookmarks.find((b) => b.id == bookmarkId));
                    //@ts-ignore
                    bookmark.id = response.data.data.bookmark_id;
                    bookmark.displayOrder = response.data.data.displayOrder;
                    bookmark.name = response.data.data.name;
                    const list = bookmarks.slice();
                    // @ts-ignore
                    list.push(bookmark);
                    setBookmarks(list);
                } else {
                    console.log(response);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const APIDeleteBookmark = (bookmarkId: number) => {
        const bookmark = getNewBookmarkObj(bookmarks.find((b) => b.id == bookmarkId));
        if (!confirm(t('confirm_delete', { type: t(bookmark?.bookmarkType ?? 'bookmark') }))) {
            return;
        }
        setIsLoading('delete');
        const list = bookmarks.slice();
        list.splice(findBookmarkIndexById(bookmarkId), 1);
        externalReportAPI.bookmark
            .deleteBookmark(elementId, elementType, bookmarkId)
            .then((response) => {
                if (response.data!.status == 'OK') {
                    setBookmarks(list);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const APISortBookmarks = (oldIndex: number, newIndex: number, managerContextType?: ManagerContextType) => {
        setIsLoading('sort');
        if (!managerContextType) {
            managerContextType = 'bookmark';
        }
        const actual = bookmarks.filter((b) => b.bookmarkType == managerContextType);
        const left = bookmarks.filter((b) => b.bookmarkType != managerContextType);
        const list = arrayMoveImmutable(actual, oldIndex, newIndex);
        const result = list.concat(left);
        externalReportAPI.bookmark
            .sortBookmarks(elementId, elementType, list, elementContext.distributionItemId)
            .then(() => {
                setBookmarks(result);
            })
            .finally(() => setIsLoading(false));
    };

    // Process initial data
    useEffect(() => {
        // if (viewerDataRequestStatus != 'success') {
        //     return;
        // }
        const filtersData = structuredClone(viewerFiltersData);
        let hasSegment = false;
        const bookmarksList = filtersData.bookmarksList.map((bookmark: BookmarkType) => {
            if (bookmark.segmentId) {
                hasSegment = true;
                // Setup SegmentFilter as filter for bookmark
                // const segmentId = bookmark.segmentId * -1;
                let parentSegmentIsSet = false;
                filtersList.forEach((filter: FilterType) => {
                    if (!filter.isSegment) {
                        return;
                    }

                    let segmentValueId = bookmark.segmentValueId;
                    if (bookmark.parentSegmentValueId > 0) {
                        segmentValueId = Number(
                            parentSegmentIsSet ? bookmark.segmentValueId : bookmark.parentSegmentValueId,
                        );
                        parentSegmentIsSet = true;
                    }

                    const segmentValueInfo = segmentValuesMap?.[filter.filterId]?.find(
                        (value: any) => value.value_id == segmentValueId,
                    );

                    if (!segmentValueInfo) {
                        return;
                    }

                    bookmark.filtersList.unshift({
                        filterId: filter.filterId,
                        values: [
                            {
                                value_id: segmentValueId,
                                value: segmentValueInfo.value,
                                display_value: segmentValueInfo.display_value,
                                isVisible: true,
                            },
                        ],
                        selectedValues: [bookmark.segmentValueId],
                        filterInput: 'single',
                    });
                });
            }

            bookmark.filtersList = bookmark.filtersList.map((filter) => {
                const fullFilter = filtersList.find((f: FilterType) =>
                    filter.filterId < 0 ? f.filterId == filter.filterId * -1 : f.filterId == filter.filterId,
                );
                if (fullFilter) {
                    filter.filterInput = fullFilter.filterInput ?? 'single';
                }
                if (filter.filterInput == 'text') {
                    filter.selectedValues = filter.values.map((v) => v.value);
                } else {
                    filter.selectedValues = filter.values.map((v) => v.value_id);
                }
                return filter;
            });
            bookmark.bookmarkType = bookmark.bookmarkType ? bookmark.bookmarkType : 'bookmark';
            return bookmark;
        });
        // Find default bookmark
        if (
            filtersData?.liveFilters !== true &&
            filtersData.selectedBookmarkId == 0 &&
            bookmarksList.length != 0 &&
            !hasSegment
        ) {
            // Select first Bookmark
            filtersData.selectedBookmarkId = bookmarksList[0].id;
        }
        setBookmarks(bookmarksList);
        setFiltersData(filtersData);
    }, [JSON.stringify(viewerFiltersData)]);

    return {
        bookmarks,
        setBookmarks,
        filtersData,
        appliedBookmarkId,
        setAppliedBookmarkId,
        APIUpdateBookmark,
        APISaveAsNewBookmark,
        APIDeleteBookmark,
        APIDuplicateBookmark,
        APISortBookmarks,
        APIIsLoading: isLoading,
    };
}
